import { forwardRef, HTMLAttributes, ReactNode } from "react";

import EyeIcon from "../../../../icons/Eye";
import SortIcon from "../../../../icons/Sort";
import { ActionButton, Card, Tile } from "./styles";

type Props = HTMLAttributes<HTMLDivElement> & {
  title: string;
  /** Shown right after the title (e.g. the PLUS tag). */
  titleExtra?: ReactNode;
  /** Second, smaller line under the title (e.g. "Telefone · Obrigatório"). */
  description?: string;
  /** Content of the 40px square before the title; the square is omitted when absent. */
  leading?: ReactNode;
  /** "icon": a section icon on a colored square; "media": an image or emoji on white. */
  leadingVariant?: "icon" | "media";
  /** Hidden/inactive rows: grayed title and icon. */
  dimmed?: boolean;
  /** Buttons at the end of the row (use `ListCardAction`). */
  actions?: ReactNode;
  /** Rows that can't be reordered (e.g. the Keepo's home among its pages): no drag handle. */
  hideDragHandle?: boolean;
};

/**
 * Row of the editor's sortable lists - the sections of a Keepo and the items of a section (links,
 * catalog, FAQ, highlights). Forwards ref and props so the drag-and-drop library can wrap it.
 */
export const ListCard = forwardRef<HTMLDivElement, Props>(
  (
    { title, titleExtra, description, leading, leadingVariant = "media", dimmed, actions, hideDragHandle, ...rest },
    ref
  ) => (
    <Card ref={ref} $dimmed={dimmed} {...rest}>
      {!hideDragHandle && (
        <span className="drag-handle" aria-hidden="true">
          <SortIcon color="currentColor" />
        </span>
      )}
      {leading && (
        <Tile $variant={leadingVariant} $dimmed={dimmed}>
          {leading}
        </Tile>
      )}
      <div className="text">
        <div className="title">
          <span>{title}</span>
          {titleExtra}
        </div>
        {description && <span className="description">{description}</span>}
      </div>
      {actions && <div className="actions">{actions}</div>}
    </Card>
  )
);

ListCard.displayName = "ListCard";

export const ListCardAction = ActionButton;

const EyeOffIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <path d="M1 1l22 22" />
  </svg>
);

/** Icon of the show/hide action: an eye, crossed out while the row is hidden. */
export const VisibilityIcon = ({ visible }: { visible: boolean }) =>
  visible ? <EyeIcon width={16} height={13} color="currentColor" /> : <EyeOffIcon />;
